// src/components/GeneratedStandings.tsx - Refactored with shared hooks
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Search, X, Pin, PinOff } from 'lucide-react';
import { cn } from '../lib/utils';
import { useConfig } from '../context/ConfigContext';
import { WinnerWithMetadata, WinnerSource } from '../types/types';
import { convertPlayerPicksToScenario } from '../utils/playerPicksUtils';
import { usePlayerScores, PlayerScoreInfo } from '../hooks/usePlayerScores';
import { useChangeDetection } from '../hooks/useChangeDetection';
import { usePinnedPlayers } from '../hooks/usePinnedPlayers';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Input } from './ui/input';
import { Button } from './ui/button';
import Pagination from './Pagination';
import InfoTooltip from './InfoTooltip';
import { useSwipePagination } from '../hooks/useSwipePagination';
import { usePageTransition } from '../hooks/usePageTransition';

const PAGINATION_THRESHOLD = 20;

// Inner component so useSwipePagination can access currentPage/totalPages as props
const StandingsTable: React.FC<{
  items: PlayerScoreInfo[];
  isFilterActive: boolean;
  showPagination: boolean;
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  pageSize: number;
  bracketComplete: boolean;
  renderStandingsRow: (player: PlayerScoreInfo) => React.ReactNode;
}> = ({ items, isFilterActive, showPagination, totalPages, currentPage, onPageChange, totalItems, pageSize, bracketComplete, renderStandingsRow }) => {
  const { handlePageChange, slideClass } = usePageTransition(onPageChange, currentPage);
  const swipeHandlers = useSwipePagination(currentPage, totalPages, handlePageChange);

  return (
    <div className="overflow-x-auto mb-4" {...(showPagination ? swipeHandlers : {})}>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-7 text-right pr-1 px-0.5">#</TableHead>
            <TableHead className="text-left px-1">
              Player
              {!bracketComplete && (
                <InfoTooltip content={<p>Click on a player's name to load their picks into the scenario builder. Existing scenario will be overwritten.</p>} />
              )}
            </TableHead>
            <TableHead className="text-center px-0.5 leading-tight">Current<br />Score</TableHead>
            <TableHead className="text-center px-0.5 leading-tight" colSpan={3}>Scenario<br />Score</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className={slideClass}>
          {items.length === 0 && isFilterActive ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center italic text-muted-foreground">
                No players match your search
              </TableCell>
            </TableRow>
          ) : (
            items.map(player => renderStandingsRow(player))
          )}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          pageSize={pageSize}
        />
      )}
    </div>
  );
};

interface GeneratedStandingsProps {
  playerPicks: string;
  winnersWithMetadata: WinnerWithMetadata[];
  bracketComplete?: boolean;
  // New prop for loading player scenario
  onLoadPlayerScenario?: (winners: WinnerWithMetadata[], playerName: string, validationError?: string) => void;
  // Ref for the header element (used by IntersectionObserver for floating standings)
  headerRef?: React.RefObject<HTMLDivElement | null>;
  // Pinned players (controlled by parent for bidirectional sync)
  pinnedPlayers?: string[];
  // Callback to notify parent of pinned players changes
  onPinnedPlayersChange?: (pinnedPlayers: string[]) => void;
  // Dynamic page size (from useDynamicPageSize hook)
  pageSize?: number;
}

const GeneratedStandings: React.FC<GeneratedStandingsProps> = ({
  playerPicks,
  winnersWithMetadata,
  bracketComplete = false,
  onLoadPlayerScenario,
  headerRef,
  pinnedPlayers = [],
  onPinnedPlayersChange,
  pageSize = 15
}) => {
  const { playerScores, rankMap, officialWinners, scenarioWinners } = usePlayerScores(playerPicks, winnersWithMetadata);
  const { changeMap } = useChangeDetection(playerScores);
  const { togglePin, pinToast, MAX_PINS } = usePinnedPlayers(pinnedPlayers, onPinnedPlayersChange);
  const [currentPage, setCurrentPage] = useState(1);

  // Search filter state
  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get the scoring configuration from context (for tooltip display)
  const { currentPointValues } = useConfig();

  // Search debounce
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedFilter(searchFilter);
    }, 150);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchFilter]);

  // Extract only official winners for the scenario conversion
  const officialWinnersForScenario = useMemo(() => {
    // Don't filter - maintain the full array with indices
    // This ensures that official winners retain their proper game positions
    const officialArray: WinnerWithMetadata[] = [];
    winnersWithMetadata.forEach((winner, index) => {
      if (winner && winner.source === WinnerSource.OFFICIAL) {
        officialArray[index] = winner;
      }
    });
    return officialArray;
  }, [winnersWithMetadata]);

  // Handle player name click to load their picks as a scenario
  const handlePlayerClick = (playerName: string) => {
    if (bracketComplete || !onLoadPlayerScenario) {
      return; // Don't allow clicks if bracket is complete or no handler provided
    }

    // Convert player picks to scenario format
    const { winners, validationError, playerName: convertedPlayerName } = convertPlayerPicksToScenario(
      playerName,
      playerPicks,
      officialWinnersForScenario
    );

    // Call the parent handler
    onLoadPlayerScenario(winners, convertedPlayerName, validationError);
  };

  // Count winners for informational purposes
  const officialWinnersCount = officialWinners.filter(winner => winner && winner.trim() !== '').length;
  const scenarioWinnersCount = scenarioWinners.filter(winner => winner && winner.trim() !== '').length;

  // Render rank change indicator
  const renderRankIndicator = useCallback((playerName: string) => {
    const change = changeMap.get(playerName);
    if (!change || change.rankDelta === 0) return null;
    if (change.rankDelta < 0) {
      return <span className="text-success text-[10px] mr-1">&#9650;</span>;
    }
    return <span className="text-destructive text-[10px] mr-1">&#9660;</span>;
  }, [changeMap]);

  // Validate pinned players against current data (drop stale names)
  const validPinnedPlayers = useMemo(() => {
    const playerNames = new Set(playerScores.map(p => p.name));
    return pinnedPlayers.filter(name => playerNames.has(name));
  }, [pinnedPlayers, playerScores]);

  // Pinned players data (sorted by scenario score desc)
  const pinnedPlayerData = useMemo(() => {
    return playerScores.filter(p => validPinnedPlayers.includes(p.name));
  }, [playerScores, validPinnedPlayers]);

  // Filtered player scores for main table
  const filteredPlayerScores = useMemo(() => {
    if (!debouncedFilter) return playerScores;
    const lower = debouncedFilter.toLowerCase();
    return playerScores.filter(p => p.name.toLowerCase().includes(lower));
  }, [playerScores, debouncedFilter]);

  // Helper to render a standings row (used for both pinned section and main table)
  const renderStandingsRow = (player: PlayerScoreInfo, isPinnedSection: boolean = false) => {
    const change = changeMap.get(player.name);
    const isFlashing = change?.scoreChanged;
    const isPinned = validPinnedPlayers.includes(player.name);
    const playerRank = rankMap.get(player.name) || 0;

    return (
      <TableRow
        key={`${isPinnedSection ? 'pin-' : ''}${player.name}`}
        className={cn(isFlashing && "standings-row-flash")}
      >
        <TableCell className="text-right pr-1 w-7 py-3 px-0.5">
          {playerRank}
        </TableCell>
        <TableCell
          className={cn(
            "max-w-[120px] truncate px-1.5 py-3",
            !bracketComplete && "text-primary cursor-pointer hover:underline",
            isPinnedSection && "text-[13px] px-1"
          )}
          onClick={() => !bracketComplete && handlePlayerClick(player.name)}
          title={player.name}
        >
          {player.name}
        </TableCell>
        <TableCell className={cn("text-center w-10 py-3 px-0", isPinnedSection && "text-[13px]")}>
          {player.currentScore}
        </TableCell>
        <TableCell className={cn("text-right w-10 py-3 px-0", isPinnedSection && "text-[13px]")}>
          {player.scenarioScore}
        </TableCell>
        <TableCell className="w-4 text-center py-3 px-0">
          {renderRankIndicator(player.name)}
        </TableCell>
        <TableCell className="w-6 text-center py-3 px-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => { e.stopPropagation(); togglePin(player.name); }}
            title={isPinned ? 'Unpin player' : 'Pin player'}
          >
            {isPinned
              ? <Pin className="h-4 w-4 text-warning fill-warning" />
              : <PinOff className="h-4 w-4 text-muted-foreground" />
            }
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  // Whether filter is active (disables pagination)
  const isFilterActive = debouncedFilter.length > 0;

  return (
    <Card className="my-5">
      <style>{`
        @keyframes standings-flash {
          0% { background-color: hsl(var(--warning) / 0.15); }
          100% { background-color: transparent; }
        }
        .standings-row-flash {
          animation: standings-flash 2s ease-out;
        }
      `}</style>
      <CardHeader>
        <div ref={headerRef} className="flex items-center">
          <CardTitle>Generated Standings</CardTitle>
          <InfoTooltip
            content={
              <>
                <p>Current scores reflect points from {officialWinnersCount} official known winners only.</p>
                {scenarioWinnersCount > officialWinnersCount && (
                  <>
                    <br />
                    <p>Scenario scores include {scenarioWinnersCount} winners (official + your selections).</p>
                  </>
                )}
                <br />
                <p>Scoring: {currentPointValues.join(', ')} points per round</p>
              </>
            }
          />
        </div>
      </CardHeader>
      <CardContent>
        {playerScores.length > 0 ? (
          <>
            {/* Pinned Players Section */}
            {pinnedPlayerData.length > 0 && (
              <div className="bg-primary/10 mb-3">
                <div className="text-xs text-muted-foreground font-medium mb-1.5 px-1">Pinned Players</div>
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-7 text-right pr-1 px-0.5 h-8">#</TableHead>
                      <TableHead className="text-left px-1 h-8">Player</TableHead>
                      <TableHead className="text-center px-0.5 leading-tight">Current<br />Score</TableHead>
                      <TableHead className="text-center px-0.5 leading-tight" colSpan={3}>Scenario<br />Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pinnedPlayerData.map(player => renderStandingsRow(player, true))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Search Filter */}
            <div className="mb-2.5">
              <div className="relative flex items-center">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Search players..."
                  className="pl-7 pr-7 h-8 text-[13px]"
                />
                {searchFilter && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                    onClick={() => setSearchFilter('')}
                  >
                    <X className="h-4 w-4 text-muted-foreground" />
                  </Button>
                )}
              </div>
              {isFilterActive && (
                <div className="text-xs text-muted-foreground mt-1">
                  Showing {filteredPlayerScores.length} of {playerScores.length} players
                </div>
              )}
            </div>

            {/* Pin limit toast */}
            {pinToast && (
              <div className="bg-warning/10 border border-warning/40 rounded-md py-1.5 px-3 text-[13px] text-warning-foreground mb-2 text-center">
                Maximum of {MAX_PINS} pinned players. Unpin one to add another.
              </div>
            )}

            {/* Main Standings Table */}
            {(() => {
              const dataSource = filteredPlayerScores;
              const showPagination = !isFilterActive && dataSource.length > PAGINATION_THRESHOLD;
              const totalPages = showPagination ? Math.ceil(dataSource.length / pageSize) : 1;
              const items = showPagination
                ? dataSource.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize)
                : dataSource;

              return (
                <StandingsTable
                  items={items}
                  isFilterActive={isFilterActive}
                  showPagination={showPagination}
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  totalItems={dataSource.length}
                  pageSize={pageSize}
                  bracketComplete={bracketComplete}
                  renderStandingsRow={renderStandingsRow}
                />
              );
            })()}

          </>
        ) : (
          <div className="p-5 text-center italic text-muted-foreground">
            No player data available. Please enter player picks to see the standings.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GeneratedStandings;
